import React, { useMemo, useState } from 'react';
import Topbar, { type BreadcrumbItem } from './Topbar';
import Sidebar from './Sidebar';
import { useAuth } from '../../lib/auth';
import MachinesIndexPage from '../../features/machines/index';
import MachineCreatePage from '../../features/machines/create';
import MachineUpdatePage from '../../features/machines/update';
import MachineViewPage from '../../features/machines/view';
import ProductsIndexPage from '../../features/products/index';
import ProductCreatePage from '../../features/products/create';
import ProductUpdatePage from '../../features/products/update';
import ProductViewPage from '../../features/products/view';
import DashboardPage from '../../features/dashboard/index';
import VideosIndexPage from '../../features/videos/index';
import VideoCreatePage from '../../features/videos/create';
import VideoViewPage from '../../features/videos/view';
import AssignVideosPage from '../../features/machines/assign-videos';
import TransactionsIndexPage from '../../features/transactions/index';
import TransactionViewPage from '../../features/transactions/view';
import SettingsPaymentPage from '../../features/settings/payment';
import SettingsListPage from '../../features/settings/index';
import PaymentSettingsViewOnlyPage from '../../features/settings/payment/viewOnly';
import { LayoutDashboard, Cpu, Package, Film, Receipt } from 'lucide-react';

export type Route =
  | { name: 'dashboard' }
  | { name: 'machines/index' }
  | { name: 'machines/create' }
  | { name: 'machines/update'; params: { id: string } }
  | { name: 'machines/view'; params: { id: string } }
  | { name: 'machines/assign-videos'; params: { id: string } }
  | { name: 'products/index' }
  | { name: 'products/create' }
  | { name: 'products/update'; params: { id: string } }
  | { name: 'products/view'; params: { id: string } }
  | { name: 'videos/index' }
  | { name: 'videos/create' }
  | { name: 'videos/view'; params: { id: string } }
  | { name: 'transactions/index' }
  | { name: 'transactions/view'; params: { id: string } }
  | { name: 'settings/index' }
  | { name: 'settings/payment' }
  | { name: 'settings/payment/view' };

type Props = {
  onLogout?: () => void;
};

export const ProtectedLayout: React.FC<Props> = ({ onLogout }) => {
  const { user } = useAuth();
  const [route, setRoute] = useState<Route>({ name: 'dashboard' });

  // Client-side routing with History API: map Route <-> path and sync state with URL
  // NOTE: Your server must be configured to serve index.html for these SPA routes (fallback to 200 on unknown paths).
  const routeToPath = (r: Route): string => {
    switch (r.name) {
      case 'dashboard':
        return '/dashboard';
      case 'machines/index':
        return '/machines';
      case 'machines/create':
        return '/machines/create';
      case 'machines/update':
        return `/machines/${r.params.id}/update`;
      case 'machines/view':
        return `/machines/${r.params.id}`;
      case 'machines/assign-videos':
        return `/machines/${r.params.id}/assign-videos`;
      case 'products/index':
        return '/products';
      case 'products/create':
        return '/products/create';
      case 'products/update':
        return `/products/${r.params.id}/update`;
      case 'products/view':
        return `/products/${r.params.id}`;
      case 'videos/index':
        return '/videos';
      case 'videos/create':
        return '/videos/create';
      case 'videos/view':
        return `/videos/${r.params.id}`;
      case 'transactions/index':
        return '/transactions';
      case 'transactions/view':
        return `/transactions/${(r as any).params.id}`;
      case 'settings/index':
        return '/settings';
      case 'settings/payment':
        return '/settings/payment';
      case 'settings/payment/view':
        return '/settings/payment/view';
      default:
        return '/dashboard';
    }
  };

  const pathToRoute = (path: string): Route => {
    const p = path.startsWith('/') ? path : `/${path}`;
    const parts = p.split('/').filter(Boolean); // e.g., ["machines", "123", "update"]

    if (parts.length === 0) return { name: 'dashboard' };

    if (parts[0] === 'dashboard') return { name: 'dashboard' };

    if (parts[0] === 'machines') {
      if (parts.length === 1) return { name: 'machines/index' };
      if (parts[1] === 'create') return { name: 'machines/create' };
      if (parts.length >= 2 && parts[2] === 'update') return { name: 'machines/update', params: { id: parts[1] } } as any;
      if (parts.length >= 2 && parts[2] === 'assign-videos') return { name: 'machines/assign-videos', params: { id: parts[1] } } as any;
      return { name: 'machines/view', params: { id: parts[1] } } as any;
    }

    if (parts[0] === 'products') {
      if (parts.length === 1) return { name: 'products/index' };
      if (parts[1] === 'create') return { name: 'products/create' };
      if (parts.length >= 2 && parts[2] === 'update') return { name: 'products/update', params: { id: parts[1] } } as any;
      return { name: 'products/view', params: { id: parts[1] } } as any;
    }

    if (parts[0] === 'videos') {
      if (parts.length === 1) return { name: 'videos/index' };
      if (parts[1] === 'create') return { name: 'videos/create' };
      return { name: 'videos/view', params: { id: parts[1] } } as any;
    }

    if (parts[0] === 'transactions') {
      if (parts.length === 1) return { name: 'transactions/index' };
      return { name: 'transactions/view', params: { id: parts[1] } } as any;
    }

    if (parts[0] === 'settings') {
      if (parts.length === 1) return { name: 'settings/index' };
      if (parts[1] === 'payment' && parts.length === 2) return { name: 'settings/payment' } as any;
      if (parts[1] === 'payment' && parts[2] === 'view') return { name: 'settings/payment/view' } as any;
    }

    return { name: 'dashboard' };
  };

  React.useEffect(() => {
    // initialize from current path
    const initial = pathToRoute(window.location.pathname || '/dashboard');
    setRoute(initial);

    const onPopState = () => {
      const r = pathToRoute(window.location.pathname || '/dashboard');
      setRoute(r);
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  const navigate = (r: Route) => {
    const newPath = routeToPath(r);
    if (window.location.pathname !== newPath) {
      window.history.pushState({}, '', newPath);
    }
    // Update state even if same path to force refresh where needed
    setRoute(r);
  };

  const menu = useMemo(
    () => {
      const allItems = [
        { key: 'dashboard', label: 'Dashboard', icon: <LayoutDashboard className="h-4 w-4" /> },
        { key: 'machines/index', label: 'Machines', icon: <Cpu className="h-4 w-4" /> },
        { key: 'products/index', label: 'Products', icon: <Package className="h-4 w-4" /> },
        { key: 'transactions/index', label: 'Transactions', icon: <Receipt className="h-4 w-4" /> },
        { key: 'videos/index', label: 'Videos', icon: <Film className="h-4 w-4" /> },
      ];

      const isAdminVm = user?.username === 'admin_vm' || user?.email === 'admin_vm';
      if (isAdminVm) {
        return allItems.filter(item => ['dashboard', 'transactions/index'].includes(item.key));
      }

      return allItems;
    },
    [user]
  );


  const breadcrumbs: BreadcrumbItem[] = (() => {
    const items: BreadcrumbItem[] = [];
    const add = (label: string, to?: Route) => items.push({ label, onClick: to ? () => navigate(to) : undefined });

    switch (route.name) {
      case 'dashboard':
        add('Dashboard');
        break;

      case 'machines/index':
        add('Machines');
        break;
      case 'machines/create':
        add('Machines', { name: 'machines/index' });
        add('Create');
        break;
      case 'machines/update':
        add('Machines', { name: 'machines/index' });
        add('Update');
        break;
      case 'machines/view':
        add('Machines', { name: 'machines/index' });
        add('View');
        break;
      case 'machines/assign-videos':
        add('Machines', { name: 'machines/index' });
        add('View', { name: 'machines/view', params: { id: (route as any).params.id } });
        add('Assign Videos');
        break;

      case 'products/index':
        add('Products');
        break;
      case 'products/create':
        add('Products', { name: 'products/index' });
        add('Create');
        break;
      case 'products/update':
        add('Products', { name: 'products/index' });
        add('Update');
        break;
      case 'products/view':
        add('Products', { name: 'products/index' });
        add('View');
        break;

      case 'transactions/index':
        add('Transactions');
        break;
      case 'transactions/view':
        add('Transactions', { name: 'transactions/index' });
        add('View');
        break;

      case 'videos/index':
        add('Videos');
        break;
      case 'videos/create':
        add('Videos', { name: 'videos/index' });
        add('Create');
        break;
      case 'videos/view':
        add('Videos', { name: 'videos/index' });
        add('View');
        break;

      case 'settings/index':
        add('Settings');
        break;
      case 'settings/payment':
        add('Settings', { name: 'settings/index' });
        add('Payment Gateway');
        break;
      case 'settings/payment/view':
        add('Settings', { name: 'settings/index' });
        add('Payment Gateway', { name: 'settings/payment' });
        add('View');
        break;

      default:
        add('Dashboard');
        break;
    }

    return items;
  })();

  return (
    <div className="flex h-screen">
      <Sidebar
        items={menu}
        activeKey={route.name}
        onSelect={key =>
          navigate(
            key === 'dashboard'
              ? { name: 'dashboard' }
              : key === 'machines/index'
              ? { name: 'machines/index' }
              : key === 'products/index'
              ? { name: 'products/index' }
              : key === 'transactions/index'
              ? { name: 'transactions/index' }
              : key === 'videos/index'
              ? { name: 'videos/index' }
              : { name: 'dashboard' }
          )
        }
      />
      <div className="flex min-w-0 flex-1 flex-col">
        <Topbar breadcrumbs={breadcrumbs} onLogout={onLogout} onSettings={() => navigate({ name: 'settings/index' })} />
        <div className="min-h-0 flex-1 overflow-auto p-4">
          {route.name === 'dashboard' && <DashboardPage />}
          {route.name === 'machines/index' && <MachinesIndexPage navigate={navigate} />}
          {route.name === 'machines/create' && <MachineCreatePage navigate={navigate} />}
          {route.name === 'machines/update' && <MachineUpdatePage id={route.params.id} navigate={navigate} />}
          {route.name === 'machines/view' && <MachineViewPage id={route.params.id} navigate={navigate} />}
          {route.name === 'machines/assign-videos' && <AssignVideosPage machineId={route.params.id} navigate={navigate} />}

          {route.name === 'products/index' && <ProductsIndexPage navigate={navigate} />}
          {route.name === 'products/create' && <ProductCreatePage navigate={navigate} />}
          {route.name === 'products/update' && <ProductUpdatePage id={route.params.id} navigate={navigate} />}
          {route.name === 'products/view' && <ProductViewPage id={route.params.id} navigate={navigate} />}

          {route.name === 'transactions/index' && <TransactionsIndexPage navigate={navigate} />}
          {route.name === 'transactions/view' && <TransactionViewPage id={(route as any).params.id} navigate={navigate} />}

          {route.name === 'videos/index' && <VideosIndexPage navigate={navigate} />}
          {route.name === 'videos/create' && <VideoCreatePage navigate={navigate} />}
          {route.name === 'videos/view' && <VideoViewPage id={route.params.id} navigate={navigate} />}

          {route.name === 'settings/index' && <SettingsListPage navigate={navigate} />}
          {route.name === 'settings/payment/view' && <PaymentSettingsViewOnlyPage navigate={navigate} />}
          {route.name === 'settings/payment' && <SettingsPaymentPage navigate={navigate} />}
        </div>
      </div>
    </div>
  );
};

export default ProtectedLayout;
